import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import EmptyState from "./EmptyState";
import TooltipUI from "./Tooltip";
import InfoIcon from "./InfoIcon";

const SentimentDistributionChart = ({ data }) => {
  // Check if we have actual data
  const hasData = data && data.length > 0 && data.some(item => item.value > 0);
  
  if (!hasData) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle">
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Sentiment distribution</h3>
          <TooltipUI
            content="Overall sentiment mix (positive / neutral / negative) across the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <EmptyState
          variant="no-data"
          title="No sentiment data available"
          description="There's no sentiment analysis data for the selected period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  // Use the actual data
  const chartData = data;

  // Calculate total percentage
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // Calculate most frequent sentiment
  const getMostFrequentSentiment = () => {
    if (!chartData || chartData.length === 0 || total === 0) return { name: "Balanced", color: "#ABABBA" };
    
    const maxItem = chartData.reduce((max, current) => 
      current.value > max.value ? current : max
    );
    
    return maxItem;
  };

  const mostFrequentSentiment = getMostFrequentSentiment();

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
      
      console.log('Sentiment tooltip data:', { 
        name: item.name, 
        value: item.value, 
        percentage 
      });
      
      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium" style={{ color: item.color }}>{item.name}</p>
          <p className="text-sm text-grey-60">
            <span className="font-medium">{percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex justify-center gap-4 mt-4">
        {payload.map((entry, index) => (
          <div key={`legend-${index}`} className="flex items-center">
            <div 
              className="w-3 h-3 rounded-full mr-1" 
              style={{ backgroundColor: entry.color }}
            ></div>
            <span className="text-xs text-grey-60">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Debug log the data being passed to the chart
  console.log('SentimentDistributionChart received data:', chartData);

  return (
    <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle h-full flex flex-col">
      <div className="mb-4 flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">Sentiment distribution</h3>
          <TooltipUI
            content="Overall sentiment mix (positive / neutral / negative) across the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <div className="flex items-baseline gap-2">
          <p className="font-medium text-[18px] text-black-0">
            {mostFrequentSentiment.name}
          </p>
          <span className="text-[12px] text-grey-60">is dominant sentiment</span>
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center">
        <div className="flex-1 w-full max-w-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4">
          <CustomLegend payload={chartData.map(item => ({ value: item.name, color: item.color }))} />
        </div>
      </div>
    </div>
  );
};

export default SentimentDistributionChart; 